import { TechnologyDropdown } from 'domain/shared/career';
import { PositionDropdown } from 'domain/shared/employee';
import { EMPLOYEE_COMPETENCY_LEVEL } from 'domain/shared/performanceMeasurement';
import { CheckboxGroup, ClearAfterUnmountWrapper, EnumDropdown } from 'framework/form';
import type { FormFieldsData } from 'framework/form';
import { CreateEditFormDrawer } from 'framework/ViewTypes/contentLayout';
import type { CreateEditFormField, CreateEditFormSection } from 'framework/ViewTypes/contentLayout';
import { applicationStages } from '../constants';
import { CHECK_JOBS } from '../candidateChecks';
import type { ApplicationRow } from '../types';
import applicationEditValidation from '../validation/applicationEditValidation';
import { getTechnologyNameFilter } from './technologyNameFilter';

type ApplicationEditDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  application: ApplicationRow;
};

const CANDIDATE_CHECK_OPTIONS = [
  { value: 'screening-checklist', label: 'Screening checklist' },
  { value: 'general-interview', label: 'General interview' },
  { value: 'screening-test-task', label: 'Screening test task' },
  { value: 'role-related-interview', label: 'Role related interview' },
  { value: 'screening-interview', label: 'Screening interview' },
  { value: 'client-interview', label: 'Client interview' },
  { value: 'executive-interview', label: 'Executive interview' },
];

const STAGE_FIELD: CreateEditFormField = {
  id: 'stage',
  content: <EnumDropdown source="stage" label="Stage" options={applicationStages} isRequired />,
};

const SPECIALIZATION_FIELD: CreateEditFormField = {
  id: 'specialization',
  content: <PositionDropdown source="specialization" label="Specialization" isValueEqLabel isRequired />,
};

const LEVEL_FIELD: CreateEditFormField = {
  id: 'level',
  content: <EnumDropdown source="level" label="Level" options={EMPLOYEE_COMPETENCY_LEVEL} isRequired />,
};

const CHECKS_FIELD: CreateEditFormField = {
  id: 'checks',
  size: { xs: 12, md: 12, xl: 12 },
  content: (
    <CheckboxGroup
      source="checks"
      label="Required candidate checks"
      options={CANDIDATE_CHECK_OPTIONS}
      checkedValues={CHECK_JOBS.map(job => job.id)}
    />
  ),
};

const getEditFormSections = (data: FormFieldsData): CreateEditFormSection[] => {
  const specialization = data.specialization as string | undefined;
  const technologyFilter = getTechnologyNameFilter(specialization);

  const technologyFields: CreateEditFormField[] = technologyFilter
    ? [
        {
          id: 'technology',
          content: (
            <ClearAfterUnmountWrapper>
              <TechnologyDropdown
                source="technology"
                label="Technology"
                customReduce={technologyFilter}
                deps={[specialization]}
              />
            </ClearAfterUnmountWrapper>
          ),
        },
      ]
    : [];

  return [
    { id: 'stage', fields: [STAGE_FIELD] },
    { id: 'checks', fields: [CHECKS_FIELD] },
    { id: 'candidateProfile', fields: [SPECIALIZATION_FIELD, ...technologyFields, LEVEL_FIELD] },
  ];
};

const ApplicationEditDrawer = ({ isOpen, onClose, application }: ApplicationEditDrawerProps) => (
  <CreateEditFormDrawer
    isOpen={isOpen}
    onClose={onClose}
    title="Edit application"
    initialData={{
      stage: application.stage,
      specialization: application.specialization,
      technology: application.technology,
      level: application.level,
      checks: CHECK_JOBS.map(job => job.id),
    }}
    isCreateRequest={false}
    getValidationSchemaFunc={() => Promise.resolve(applicationEditValidation)}
    submitFormFunc={async () => {}}
    afterSubmit={{ execute: onClose, message: 'Application updated.' }}
    submitButtonTitle="Save"
    handleDirty={false}
    getSections={getEditFormSections}
  />
);

export default ApplicationEditDrawer;
